import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, CheckCircle2, Clock, Loader2, QrCode, Smartphone, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function BookingPayment({ patient, doctor, department, consultationType, disease, onReset, onBack, transferOldId }) {
  const { t } = useLang();
  const [slotsData, setSlotsData] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [paying, setPaying] = useState(false);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    api.slots(doctor.id).then(setSlotsData);
  }, [doctor.id]);

  const confirmPayment = async () => {
    setPaying(true);
    try {
      const payload = {
        patient: { ...patient, age: Number(patient.age) },
        doctor_id: doctor.id,
        department: department || doctor.department,
        slot_date: date,
        slot_time: time,
        consultation_type: consultationType,
        disease: disease || null,
      };
      const appt = await api.book(payload);

      if (transferOldId) {
        await api.transferRebook({
          old_appointment_id: transferOldId,
          correct_disease: disease || "unspecified",
          new_department: department || doctor.department,
        });
        toast.success(t("refund_success") + " · ₹" + (appt.fee || 0));
      }
      setReceipt(appt);
    } catch (e) {
      toast.error("Payment failed. " + (e?.response?.data?.detail || ""));
    } finally {
      setPaying(false);
    }
  };

  if (receipt) return <Receipt appt={receipt} onReset={onReset} />;

  if (showPayment) {
    return (
      <div className="mx-auto max-w-xl py-6">
        <Button variant="ghost" onClick={() => setShowPayment(false)} data-testid="back-btn" className="mb-4">
          <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("back")}
        </Button>
        <Card className="overflow-hidden border-slate-200 bg-white">
          <div className="bg-gradient-to-br from-sky-600 to-teal-600 p-6 text-white">
            <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest opacity-90">
              <Smartphone className="h-4 w-4" /> {t("upi_title")}
            </div>
            <div className="text-3xl font-bold">₹{doctor.fee}</div>
            <div className="mt-1 text-sm opacity-90">{t("total")}</div>
          </div>
          <div className="p-6">
            <div className="mx-auto flex h-56 w-56 items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50" data-testid="upi-qr">
              <div className="relative">
                <QrCode className="h-40 w-40 text-slate-800" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="rounded-full bg-sky-600 p-2 text-white"><Sparkles className="h-4 w-4" /></div>
                </div>
              </div>
            </div>
            <p className="mt-4 text-center text-sm text-slate-500">{t("upi_scan")}</p>
            <Button
              onClick={confirmPayment}
              disabled={paying}
              data-testid="upi-pay-confirm-btn"
              className="mt-5 h-14 w-full bg-emerald-600 text-base font-semibold hover:bg-emerald-700"
            >
              {paying ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> {t("processing")}</> : <><CheckCircle2 className="mr-2 h-5 w-5" /> {t("simulate_pay")}</>}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl py-6">
      <Button variant="ghost" onClick={onBack} data-testid="back-btn" className="mb-4">
        <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("back")}
      </Button>
      <h2 className="mb-6 text-2xl font-bold tracking-tight sm:text-3xl">{t("select_slot")}</h2>

      <Card className="mb-6 border-slate-200 bg-white p-4">
        <div className="flex items-center gap-4">
          {doctor.photo && <img src={doctor.photo} alt="" className="h-14 w-14 rounded-full object-cover ring-2 ring-sky-100" />}
          <div>
            <div className="font-bold text-slate-900">{doctor.name}</div>
            <div className="text-sm text-slate-500">{t("fee")}: ₹{doctor.fee}</div>
          </div>
        </div>
      </Card>

      {!slotsData ? (
        <div className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid gap-4" data-testid="slot-picker">
          {slotsData.days.map((d) => {
            const dateObj = new Date(d.date + "T00:00:00");
            const dayLabel = dateObj.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
            return (
              <Card key={d.date} className="border-slate-200 bg-white p-4" data-testid={`day-${d.date}`}>
                <div className="mb-3 flex items-center gap-2 font-semibold text-slate-800">
                  <Calendar className="h-4 w-4 text-sky-600" /> {dayLabel}
                </div>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                  {d.slots.map((s) => {
                    const active = date === d.date && time === s.time;
                    return (
                      <button
                        key={s.time}
                        disabled={!s.available}
                        onClick={() => { setDate(d.date); setTime(s.time); }}
                        data-testid={`slot-${d.date}-${s.time}`}
                        className={`rounded-lg border px-2 py-2 text-sm font-medium transition ${
                          !s.available
                            ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 line-through"
                            : active
                            ? "border-sky-600 bg-sky-600 text-white shadow"
                            : "border-slate-200 bg-white text-slate-700 hover:border-sky-400 hover:bg-sky-50"
                        }`}
                      >
                        <Clock className="mr-1 inline h-3 w-3" />{s.time}
                      </button>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <div className="mt-6 flex items-center justify-end">
        <Button
          disabled={!date || !time}
          onClick={() => setShowPayment(true)}
          data-testid="proceed-payment-btn"
          className="h-12 bg-sky-600 px-6 hover:bg-sky-700 disabled:opacity-50"
        >
          {t("pay_now")} · ₹{doctor.fee}
        </Button>
      </div>
    </div>
  );
}

function Receipt({ appt, onReset }) {
  const { t } = useLang();
  const [showBanner, setShowBanner] = React.useState(true);
  return (
    <div className="mx-auto max-w-2xl py-6">
      {showBanner && (
        <Card className="mb-6 flex items-center justify-between border-emerald-300 bg-emerald-50 p-4" data-testid="sms-banner">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div className="text-sm text-emerald-900">
              <div className="font-semibold">{t("sms_sent")} +91-{appt.patient.phone.replace(/.(?=.{4})/g, "X")}</div>
              <div className="text-xs">{appt.sms_body}</div>
            </div>
          </div>
          <button onClick={() => setShowBanner(false)} className="text-xs text-emerald-700 hover:underline">Dismiss</button>
        </Card>
      )}

      <Card className="overflow-hidden border-slate-200 bg-white" data-testid="receipt-card">
        <div className="bg-gradient-to-br from-slate-900 to-slate-700 p-6 text-white">
          <div className="text-xs font-semibold uppercase tracking-widest opacity-80">{t("booking_confirmed")}</div>
          <div className="mt-2 flex items-end justify-between">
            <div>
              <div className="text-xs opacity-70">{t("token")}</div>
              <div className="font-[Outfit] text-4xl font-bold tracking-tight">{appt.token_id}</div>
            </div>
            <div className="rounded-xl bg-white p-2">
              <QrCode className="h-16 w-16 text-slate-900" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 p-6 text-sm">
          <ReceiptRow label={t("patient_name")} value={appt.patient.name} />
          <ReceiptRow label={t("doctor")} value={appt.doctor_name} />
          <ReceiptRow label={t("department")} value={appt.department} />
          <ReceiptRow label={t("date_time")} value={`${appt.slot_date} · ${appt.slot_time}`} />
          <ReceiptRow label={t("fee")} value={`₹${appt.fee}`} />
          <ReceiptRow label="Phone" value={appt.patient.phone} />
        </div>
        <div className="border-t border-slate-100 p-4">
          <Button onClick={onReset} className="h-12 w-full bg-sky-600 hover:bg-sky-700" data-testid="new-booking-btn">
            {t("new_booking")}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ReceiptRow({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-semibold text-slate-900">{value}</div>
    </div>
  );
}
